import {
  JiraExternalLogo,
  LineViewLine,
  MetricsLine,
  PaperDocLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {$convertToMarkdownString} from '@lexical/markdown';
import {EmployeeRoleLevel, JiraObject, MustAddressItem, Workstream} from 'client';
import {editorNodes} from 'components/DSYS/editor/editorNodes';
import {TRANSFORMERS} from 'components/DSYS/editor/transformers';
import {t} from 'i18next';
import {createEditor} from 'lexical';
import {ComponentType, SVGProps} from 'react';

export const convertEditorJsonToMarkdown = (jsonString?: string): string => {
  if (jsonString == null || jsonString === '') return '';
  const s = typeof jsonString === 'string' ? jsonString : JSON.stringify(jsonString);
  if (!s.trim() || s === '[object Object]') return '';

  try {
    const editorState = JSON.parse(s);

    // Create editor with all the nodes used in the app
    const editor = createEditor({
      nodes: editorNodes,
      onError: (error) => {
        console.error('Editor error during markdown conversion:', error);
      },
    });

    const parsedEditorState = editor.parseEditorState(editorState);
    if (parsedEditorState.isEmpty()) {
      return '';
    }
    editor.setEditorState(parsedEditorState);

    // Read the editor state and convert to markdown
    const markdown = editor.getEditorState().read(() => {
      return $convertToMarkdownString(TRANSFORMERS);
    });

    return markdown;
  } catch (error) {
    console.error('Failed to convert editor JSON to markdown:', error);
    return '';
  }
};

export interface MustAddressSourceLabel {
  label: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>> | null;
  author?: EmployeeRoleLevel | null;
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  previous_update: 'ai_coach_source_previous_update',
  sub_workstream: 'ai_coach_source_sub_workstream',
  metric: 'ai_coach_source_metric',
  jira: 'ai_coach_source_jira',
  parent_rules: 'ai_coach_source_parent_rules',
  general: 'ai_coach_source_general',
};

const SOURCE_TYPE_ICONS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  previous_update: LineViewLine,
  sub_workstream: WorkstreamsLine,
  metric: MetricsLine,
  jira: JiraExternalLogo,
  parent_rules: PaperDocLine,
};

export function getMustAddressSourceLabel(
  item: MustAddressItem,
  workstream: Workstream,
  jiraObjects: JiraObject[] = []
): MustAddressSourceLabel | null {
  const {source_type, source_id} = item;
  const fallbackKey = source_type ? SOURCE_TYPE_LABELS[source_type] : null;
  const fallback = fallbackKey ? t(fallbackKey) : null;

  switch (source_type) {
    case 'previous_update': {
      const latestUpdate = workstream.updates?.[0];
      return {
        label: latestUpdate?.comment ?? '',
        icon: SOURCE_TYPE_ICONS.previous_update,
        author: latestUpdate?.author ?? null,
      };
    }
    case 'sub_workstream': {
      if (source_id != null) {
        const dep = workstream.dependencies?.find((d) => d.dependent?.id === source_id);
        if (dep?.dependent)
          return {label: dep.dependent.name, icon: SOURCE_TYPE_ICONS.sub_workstream};
      }
      return fallback ? {label: fallback, icon: SOURCE_TYPE_ICONS.sub_workstream} : null;
    }
    case 'metric': {
      if (source_id != null) {
        const metric = workstream.metrics?.find((m) => m.id === source_id);
        if (metric) return {label: metric.title, icon: SOURCE_TYPE_ICONS.metric};
      }
      return fallback ? {label: fallback, icon: SOURCE_TYPE_ICONS.metric} : null;
    }
    case 'jira': {
      if (source_id != null) {
        const jira = jiraObjects.find((j) => j.id === source_id);
        if (jira) {
          const jiraUrl = `https://dropbox.atlassian.net/browse/${jira.key}`;
          return {
            label: `[${jira.key}](${jiraUrl})`,
            icon: SOURCE_TYPE_ICONS.jira,
          };
        }
      }
      return fallback ? {label: fallback, icon: SOURCE_TYPE_ICONS.jira} : null;
    }
    case 'parent_rules':
      return fallback ? {label: fallback, icon: SOURCE_TYPE_ICONS.parent_rules} : null;
    case 'general':
      return null;
    default:
      return fallback ? {label: fallback} : null;
  }
}
